import type { AllowedButtons, Driver, DriveStep } from 'driver.js';
import { filterExistingSteps } from './tourFilter';
import { createGates } from './tourGates';
import { buildLiveSessionSteps, buildRolePlaySharedSteps } from './rolePlayWalkthroughSteps';

const noNext: { showButtons: AllowedButtons[] } = { showButtons: ['close'] };

// Pagination bars are the last element on these list pages. driver.js only
// scrolls a target into view when its own internal visibility check decides
// it isn't already fully on-screen (bounding rect vs window.innerHeight) —
// a bottom-of-page element flush with the viewport edge already passes that
// check, so driver.js skips scrolling entirely, leaving no room above for
// the `side: 'top'` popover and forcing an overlap. onHighlightStarted fires
// before driver.js positions the popover/stage, so forcing a centered
// scroll here (before driver.js's own scroll-or-skip decision) guarantees
// room on both sides regardless of that internal check.
function withCenteredHighlight(step: DriveStep): DriveStep {
  const prevOnHighlightStarted = step.onHighlightStarted;
  return {
    ...step,
    onHighlightStarted: (element, s, opts) => {
      element?.scrollIntoView({ block: 'center', behavior: 'auto' });
      prevOnHighlightStarted?.(element, s, opts);
    },
  };
}

/**
 * Join action clauses into one sentence: "a", "a, or b", "a, b, or c".
 *
 * Several list pages describe an Actions column whose buttons are conditional —
 * on role, on the active tab, or on each row's status — so their copy has to be
 * assembled from what is actually in the DOM rather than written as a fixed
 * string. Naming a button the viewer doesn't have is the bug this avoids.
 *
 * The comma before "or" is kept even for two clauses, matching the copy this was
 * extracted from (buildCompetitionsSteps): individual clauses can themselves
 * contain "or" ("start or continue a competition"), where the comma is what
 * separates the alternatives from each other.
 */
function joinClauses(clauses: string[]): string {
  if (clauses.length <= 1) return clauses[0] ?? '';
  return `${clauses.slice(0, -1).join(', ')}, or ${clauses[clauses.length - 1]}`;
}

/** Capitalise the first letter, for a clause list used at the start of a sentence. */
function sentence(text: string): string {
  return text ? `${text[0].toUpperCase()}${text.slice(1)}` : '';
}

/** Replace one step's popover copy in place, matched by its element selector. */
function reviseStep(
  steps: DriveStep[],
  element: string,
  popover: { title?: string; description: string },
): DriveStep[] {
  const index = steps.findIndex((step) => step.element === element);
  if (index === -1 || !steps[index].popover) return steps;
  const next = [...steps];
  next[index] = { ...next[index], popover: { ...next[index].popover, ...popover } };
  return next;
}

/**
 * Fully click-gated, mirroring the FTUE interactive tour (see
 * interactiveSteps.ts / rolePlayWalkthroughSteps.ts) rather than a
 * click-through Next-button tour — the user must actually perform each
 * action to advance. Ends differently from FTUE: instead of navigating away
 * via "Review Session" (which would destroy this tour before a closing step
 * could show), it has the user click "New Session" to reopen the same-page
 * scenario selector, then shows a real final closing step.
 *
 * Unlike FTUE (which always starts fresh with the scenario selector already
 * open), this tour can be triggered from the header dropdown at any time:
 * - After a previous session, when the selector is closed and the page is
 *   just waiting for an avatar click — a lead-in step has the user click a
 *   student avatar first (which reopens the selector), before continuing
 *   into the shared wizard walkthrough.
 * - While a session is already in progress (selector closed, mic button
 *   present) — clicking an avatar does nothing then (GeminiDirectStreamPage's
 *   avatar-click handler no-ops while connected), so the lead-in and the
 *   whole scenario wizard are skipped entirely in favor of jumping straight
 *   into the live-session steps (mic, step guide, toolbar, transcript).
 * - Mid-transition (selector closed, but the greeting preview is still
 *   playing or the session is still connecting) — an avatar click is also a
 *   no-op here, so a plain "wait a moment" step is shown instead, gated on
 *   the session actually connecting rather than on a click.
 * - The Session Ended modal is up (selector closed, session disconnected,
 *   modal covering the classroom scene) — an avatar click can't reach the
 *   scene at all, and no further gate would ever fire (the session is over,
 *   not "about to connect"), so the lead-in, the wizard, and the "End
 *   Session" step (that button no longer exists either) are all skipped in
 *   favor of jumping straight to the three session-end button steps, which
 *   are the only real targets left on screen.
 */
function buildRolePlayPageSteps(driverRef: React.RefObject<Driver | null>): DriveStep[] {
  const gates = createGates(driverRef);
  const { gateOnClickThenAppear, gateOnDom } = gates;

  const selectorAlreadyOpen = document.querySelector('#tour-scenario-selector') !== null;
  // #tour-end-session (GeminiDirectStreamPage.tsx) renders for the entire
  // life of a connected session, so it's a reliable "a session is already
  // live" signal on its own.
  //
  // #tour-classroom-scene also carries a data-tour-phase attribute reflecting
  // exactly which of the roleplay page's mutually-exclusive states is
  // current. Relying on #tour-end-session alone left two real transitional
  // phases unhandled — the intro-audio greeting preview, and the connect
  // handshake — where the selector is already closed but the session isn't
  // connected yet either. In that window an avatar click is a guaranteed
  // no-op (see handleAvatarSelect's guard), so the old fallback — "click a
  // student avatar to open the scenario selector", gated on the selector
  // reappearing — could never advance and hung the tour forever. data-tour-
  // phase distinguishes that window ('pre-connect'/'connecting') from truly
  // idle, so a "please wait" step can be shown and gated on the real next
  // transition instead.
  const phase = document.querySelector('#tour-classroom-scene')?.getAttribute('data-tour-phase');
  const endSessionPresent = document.querySelector('#tour-end-session') !== null;
  const sessionInProgress = !selectorAlreadyOpen && (endSessionPresent || phase === 'greeting' || phase === 'live');
  const isTransitioning =
    !selectorAlreadyOpen && !sessionInProgress && (phase === 'pre-connect' || phase === 'connecting');
  // The Session Ended modal is up: no session, and no session is coming
  // (unlike 'pre-connect'/'connecting', nothing here transitions on its own —
  // it waits on one of the three modal buttons). An avatar click can't reach
  // the classroom at all, so lead-in, wizard, and the "End Session" step
  // (whose button no longer exists) are all skipped below.
  const isEnded = !selectorAlreadyOpen && !sessionInProgress && !isTransitioning && phase === 'ended';

  const leadInSteps: DriveStep[] =
    selectorAlreadyOpen || sessionInProgress || isEnded
      ? []
      : isTransitioning
        ? [
            {
              element: '#tour-classroom-scene',
              popover: {
                title: 'Get Started',
                description: 'Your session is starting. Please wait a moment.',
                ...noNext,
              },
              // No click is possible in this window (handleAvatarSelect no-ops
              // while connecting), so gate on the next real transition instead:
              // #tour-end-session mounts as soon as the session connects,
              // whichever of "greeting" or "live" it lands in next.
              ...gateOnDom(0, '#tour-end-session', 'appear'),
            },
          ]
        : [
            {
              element: '#tour-classroom-scene',
              popover: {
                title: 'Get Started',
                description: 'Click on a student avatar to open the scenario selector.',
                ...noNext,
              },
              // Gate key 0 is unused elsewhere in this tour (the welcome step has
              // no gate) — it doesn't need to match this step's real position in
              // the array, only be unique within this createGates() call.
              ...gateOnDom(0, '#tour-scenario-selector', 'appear'),
            },
          ];

  // Skip the scenario wizard entirely when a session is already in progress —
  // its steps target the scenario selector, which won't reopen from an avatar
  // click while connected. During the transitional window, the wizard would
  // also target an already-closed selector, so treat it the same way. Once
  // the modal is up there's no wizard left to walk through either.
  const walkthroughSteps =
    sessionInProgress || isTransitioning
      // Jumping straight in here (no wizard immediately before it) — wait
      // for the mic button explicitly if opened during the greeting window
      // rather than silently skipping past it (see buildLiveSessionSteps's
      // waitForMic doc comment).
      ? buildLiveSessionSteps(gates, true)
      : isEnded
        ? []
        : buildRolePlaySharedSteps(gates);

  return [
    {
      popover: {
        title: 'Role Play: Page Tutorial',
        description: "Here's a detailed walkthrough of everything on this page.",
        showButtons: ['next', 'close'],
      },
    },
    ...leadInSteps,
    // Scenario wizard through live transcript (or just the live-session
    // portion, mid-session), shared with the FTUE interactive tour (see
    // rolePlayWalkthroughSteps.ts).
    ...walkthroughSteps,
    // #tour-end-session no longer exists once the Session Ended modal is up
    // — skip straight to the modal's own buttons below.
    ...(isEnded
      ? []
      : [
          {
            element: '#tour-end-session',
            popover: {
              title: 'End Session',
              description: "Click here whenever you're ready to wrap up the session.",
              ...noNext,
            },
            // Clicking opens the Session Ended modal a render cycle later —
            // wait for its "New Session" button (not "Review Session" —
            // this tour stays on the page rather than navigating away) to
            // exist before advancing.
            ...gateOnClickThenAppear(10, '#tour-end-session', '#tour-session-end-tertiary'),
          },
        ]),
    // All three session-end buttons are informational only (Next-advance,
    // not click-gated) — actually clicking any of them for real here would
    // navigate away (Review Session), or stop and restart this very tour
    // (Practice Again / New Session: GeminiDirectStreamPage.tsx's
    // isTourActive handling can't yet tell "the Page Tutorial is active" apart
    // from "the FTUE interactive tour is active", so it always treats the
    // click as FTUE's stranded-tour recovery and restarts a fresh Page
    // Tutorial from step one instead of letting this one reach its closing
    // step). Unlike FTUE's own ending (a real click, since that tour has no
    // other way to know the user is done), the Page Tutorial can just say so
    // and move on.
    {
      element: '#tour-session-end-primary',
      popover: {
        title: 'Review Session',
        description: 'Click "Review Session" to check your performance feedback for this session.',
        showButtons: ['next', 'close'],
      },
    },
    {
      element: '#tour-session-end-secondary',
      popover: {
        title: 'Practice Again',
        description: 'Click "Practice Again" to jump straight back into a new roleplay again.',
        showButtons: ['next', 'close'],
      },
    },
    {
      element: '#tour-session-end-tertiary',
      popover: {
        title: 'Start Again',
        description: 'Click "New Session" to practice again. This brings you back to the scenario selector.',
        showButtons: ['next', 'close'],
      },
    },
    {
      popover: {
        title: "That's the Role Play page!",
        description: 'Click the Tutorial button anytime you want to see this again.',
        showButtons: ['next', 'close'],
        doneBtnText: 'Finish',
      },
    },
  ];
}

const SESSIONS_LIST_STEPS: DriveStep[] = [
  {
    popover: {
      title: 'Feedback: Page Tutorial',
      description: "Here's a detailed walkthrough of this page.",
      showButtons: ['next', 'close'],
    },
  },
  {
    element: '#tour-sessions-search',
    popover: {
      title: 'Search Sessions',
      description: 'Search by ID, number, user, or module.',
    },
  },
  {
    element: '#mode-filter',
    popover: {
      title: 'Filter by Mode',
      description: 'Narrow the list down to a specific difficulty or mode.',
    },
  },
  {
    element: '#type-filter',
    popover: {
      title: 'Filter by Type',
      description: 'Narrow the list down to a specific session type.',
    },
  },
  {
    element: '#tour-date-range-filter',
    popover: {
      title: 'Filter by Date',
      description: 'Filter sessions by a start and end date.',
    },
  },
  {
    element: '#tour-sessions-list',
    popover: {
      title: 'Session History',
      description: 'Browse all past roleplay sessions.',
    },
  },
  {
    element: '#tour-sessions-actions-col',
    popover: {
      // Rewritten by buildSessionsListSteps when the row also carries the
      // request-review button; this copy is the transcript-only case.
      title: 'Actions',
      description: 'Each row has a transcript button. Click it to see that session\'s full feedback.',
    },
  },
  withCenteredHighlight({
    element: '#tour-sessions-pagination',
    popover: {
      title: 'Pagination',
      // Pagination bars sit at the bottom of these tables with little to no
      // room below for driver.js's default placement, which otherwise
      // overlaps the highlighted control instead of flipping above it.
      side: 'top',
      description: 'Move between pages, or change how many sessions are shown per page.',
    },
  }),
  {
    popover: {
      title: "That's the Feedback page!",
      description: 'Click the Tutorial button anytime you want to see this again.',
      showButtons: ['next', 'close'],
      doneBtnText: 'Finish',
    },
  },
];

/**
 * The Actions column on this page holds one or two buttons, and the second one
 * is role-gated: SessionsPage renders `.request-review-button` ("Request human
 * review") only when `canRate(userRole)`. The step used to be titled "View
 * Transcript" and describe only that button, so an admin or trainer — the only
 * roles that can request a review — was walked past the control that starts the
 * whole human-review flow without it being named.
 *
 * filterExistingSteps' presence check on the `<th>` can't see this: the header
 * renders either way. So the copy is built from which buttons are actually in
 * the DOM, the same approach buildCompetitionsSteps takes for its own
 * conditional Actions column.
 */
function buildSessionsListSteps(): DriveStep[] {
  const steps = filterExistingSteps(SESSIONS_LIST_STEPS);

  // Anchored on the header, so the step survives an empty table; the buttons are
  // per-row, so only mention the review one when a row actually offers it.
  if (document.querySelector('.request-review-button') === null) return steps;

  return reviseStep(steps, '#tour-sessions-actions-col', {
    title: 'Actions',
    description:
      sentence(
        joinClauses([
          "open a session's transcript and full feedback",
          'request a human review of it',
        ]),
      ) + ' from the Actions column. Requesting a review lets you assign it to a reviewer straight away.',
  });
}

// Session Detail has two layouts behind a floating A/B toggle (see
// SessionDetailPage.tsx): layout A is the classic three-column page where
// everything is on screen at once; layout B (SessionDetailPageV2.tsx) splits
// the same content across Overview / Teaching Steps / Transcript tabs and
// unmounts whichever tab isn't active. The step arrays below are grouped by
// *where* their anchors live so buildSessionDetailSteps can order them per
// layout — flat presence-filtering is only safe for layout A.

// Page header + score-hero KPIs + the student sidebar: on both layouts these
// sit outside the tab content, so they're visible whichever tab is open.
const SESSION_DETAIL_HEADER_STEPS: DriveStep[] = [
  {
    element: '#session-feedback-heading',
    popover: {
      title: 'Session Feedback: Page Tutorial',
      description: "Here's a detailed walkthrough of this page.",
      showButtons: ['next', 'close'],
    },
  },
  {
    element: '#tour-kpi-steps',
    popover: {
      title: 'Steps Completed',
      description: 'See how many of the teaching steps were completed in this session.',
    },
  },
  {
    element: '#tour-kpi-duration',
    popover: {
      title: 'Duration',
      description: 'How long the session lasted.',
    },
  },
  {
    element: '#tour-kpi-end-reason',
    popover: {
      title: 'Ended',
      description: 'Why the session finished: all steps done, the time limit, you ending it, or a problem such as inappropriate language.',
    },
  },
  {
    // Layout B's score hero only — filtered out on layout A, which has no
    // equivalent KPI card.
    element: '#tour-kpi-steps-included',
    popover: {
      title: 'Steps Included',
      description: 'How many teaching steps the AI actually found in the conversation, the basis for the score above.',
    },
  },
  {
    element: '#tour-kpi-rating',
    popover: {
      title: 'Trainer Rating',
      description: 'Shows whether a trainer has already rated this session.',
    },
  },
];

// Layout A: the middle feedback column. Layout B: the "Overview" tab.
const SESSION_DETAIL_OVERVIEW_STEPS: DriveStep[] = [
  {
    element: '#tour-session-feedback',
    popover: {
      title: 'AI Coaching Feedback',
      description: 'Read AI-generated coaching feedback on your performance.',
    },
  },
  {
    element: '#tour-rate-controls',
    popover: {
      title: 'Rate this Feedback',
      description: 'Trainers and admins can mark feedback as helpful or needing work. Flagging "Needs Work" opens a comment box for details.',
    },
  },
  {
    element: '#tour-session-steps',
    popover: {
      title: 'Teaching Steps',
      description: 'A checklist of the teaching method. See at a glance which steps were included and which were missed.',
    },
  },
  {
    // Filtered out by filterExistingSteps on any module whose feedback block doesn't
    // evaluate tools — the same way the social-skills step below is.
    element: '#tour-session-tools',
    popover: {
      title: 'De-escalation Tools',
      description: 'Which de-escalation tools you reached for, and which ones you missed. Tools calm an escalated student so the teaching steps can begin.',
    },
  },
  {
    element: '#tour-session-social-skills',
    popover: {
      title: 'Social Skills Feedback',
      description: 'How the session went for each social skill being practiced, with a rating and specific notes.',
    },
  },
  {
    element: '#tour-session-recommendations',
    popover: {
      title: 'Recommendations',
      description: 'Get recommended resources based on this session\'s performance.',
    },
  },
];

const SESSION_DETAIL_SIDEBAR_STEP: DriveStep = {
  element: '#tour-student-column',
  popover: {
    title: 'Student & Session Info',
    description: 'Details about the student profile and session setup.',
  },
};

const SESSION_DETAIL_TRANSCRIPT_STEPS: DriveStep[] = [
  {
    element: '#tour-session-transcript',
    popover: {
      title: 'Transcript',
      description: 'Review the full conversation transcript. Trainers and admins can rate or flag individual messages.',
    },
  },
  {
    element: '#tour-transcript-search',
    popover: {
      title: 'Search Transcript',
      description: 'Search within the conversation transcript.',
    },
  },
];

/**
 * Human Review (SessionReviewPanel). What renders depends on who's looking and
 * on review state: trainers/admins get the request → assign → review workflow,
 * a trainee gets a read-only "Coach Review" card (and nothing at all until a
 * review has been submitted). So every step here goes through
 * filterExistingSteps and only the blocks actually on screen are toured — the
 * two role-specific roots are mutually exclusive by design.
 */
const SESSION_DETAIL_HUMAN_REVIEW_STEPS: DriveStep[] = [
  {
    element: '#tour-session-human-review',
    popover: {
      title: 'Human Review',
      description: 'Beyond the AI feedback, this session can be sent to a person for review. A reviewer\'s rating and comments show up here.',
    },
  },
  {
    element: '#tour-session-coach-review',
    popover: {
      title: 'Coach Review',
      description: 'A coach has reviewed this session by hand. Here\'s their rating and what they wrote about it.',
    },
  },
  {
    element: '#tour-review-request',
    popover: {
      title: 'Request a Review',
      description: 'Set a priority, optionally note why this session needs a look, and pick who should review it. Then click "Request Human Review". They\'re notified straight away, and you\'ll get an alert when the review is ready. Leave the reviewer blank to send it to the whole review team instead.',
    },
  },
  {
    element: '#tour-review-workspace',
    popover: {
      title: 'Add Your Review',
      description: 'When a session is assigned to you, rate it and write your feedback here. Save a draft to finish later, or submit it when you\'re done.',
    },
  },
  {
    element: '#tour-review-completed',
    popover: {
      title: 'Human Feedback',
      description: 'Submitted reviews are collected here, each with the reviewer\'s rating and the AI score at the time of review.',
    },
  },
];

// Layout B's "Teaching Steps" tab — the expanded timeline behind the Overview
// tab's compact checklist. No layout A equivalent.
const SESSION_DETAIL_STEPS_TIMELINE_STEP: DriveStep = {
  element: '#tour-steps-timeline',
  popover: {
    title: 'Step-by-Step Timeline',
    description: 'Every teaching step in order, marked included or missed, with the AI\'s feedback on each one.',
  },
};

const SESSION_DETAIL_AB_TOGGLE_STEP: DriveStep = {
  element: '#tour-ab-toggle',
  popover: {
    title: 'Switch Layouts',
    description: 'Two ways to read this page: A is the classic three-column view with everything at once, B splits it into tabs. Your pick is remembered for next time.',
  },
};

const SESSION_DETAIL_CLOSING_STEP: DriveStep = {
  popover: {
    title: "That's the Session Feedback page!",
    description: 'Click the Tutorial button anytime you want to see this again.',
    showButtons: ['next', 'close'],
    doneBtnText: 'Finish',
  },
};

// Distinct cleanup keys for the gates below — see createGates, where the index
// is a map key per gated step, not a position in the step list.
const GATE_OVERVIEW_TAB = 0;
const GATE_STEPS_TAB = 1;
const GATE_TRANSCRIPT_TAB = 2;

/**
 * Layout A (classic) puts everything on one screen, so a flat
 * filterExistingSteps pass covers it.
 *
 * Layout B (SessionDetailPageV2.tsx) is tabbed and unmounts the inactive tabs,
 * so its tour walks the tab bar left to right — Overview, Teaching Steps,
 * Transcript — with a click-gated step opening each tab before touring its
 * contents, mirroring the Scene Editor toolbar fix (see buildSceneEditorSteps
 * above). Two consequences worth spelling out:
 *
 *  - Human Review lives at the bottom of the *Overview* tab, so it must be
 *    toured before any tab switch, not after the transcript as on layout A.
 *  - The tutorial can be opened from any tab, so "Overview is active" is an
 *    assumption to check, not a given — hence the gate back onto it.
 *
 * Steps behind a not-yet-open tab deliberately skip filterExistingSteps: their
 * anchors don't exist at build time, and the gate ahead of them guarantees the
 * tab is mounted before they're reached.
 */
function buildSessionDetailSteps(driverRef: React.RefObject<Driver | null>): DriveStep[] {
  const isTabbedLayout =
    document.querySelector('#tour-v2-tab-overview, #tour-v2-tab-steps, #tour-v2-tab-transcript') !== null;

  if (!isTabbedLayout) {
    return filterExistingSteps([
      ...SESSION_DETAIL_HEADER_STEPS,
      ...SESSION_DETAIL_OVERVIEW_STEPS,
      SESSION_DETAIL_SIDEBAR_STEP,
      ...SESSION_DETAIL_TRANSCRIPT_STEPS,
      ...SESSION_DETAIL_HUMAN_REVIEW_STEPS,
      SESSION_DETAIL_AB_TOGGLE_STEP,
      SESSION_DETAIL_CLOSING_STEP,
    ]);
  }

  const { gateOnClickThenAppear } = createGates(driverRef);
  const steps: DriveStep[] = filterExistingSteps(SESSION_DETAIL_HEADER_STEPS);

  const overviewTab = document.querySelector('#tour-v2-tab-overview');
  const overviewContent = [...SESSION_DETAIL_OVERVIEW_STEPS, ...SESSION_DETAIL_HUMAN_REVIEW_STEPS];
  if (document.querySelector('#tour-session-feedback') === null && overviewTab !== null) {
    steps.push(
      {
        element: '#tour-v2-tab-overview',
        popover: {
          title: 'Overview',
          description: 'Click the Overview tab. The tour starts with the session summary.',
          ...noNext,
        },
        ...gateOnClickThenAppear(GATE_OVERVIEW_TAB, '#tour-v2-tab-overview', '#tour-session-feedback'),
      },
      ...overviewContent,
    );
  } else {
    steps.push(...filterExistingSteps(overviewContent));
  }

  steps.push(...filterExistingSteps([SESSION_DETAIL_SIDEBAR_STEP]));

  if (document.querySelector('#tour-v2-tab-steps') !== null) {
    steps.push(
      {
        element: '#tour-v2-tab-steps',
        popover: {
          title: 'Teaching Steps Tab',
          description: 'Click the Teaching Steps tab for the full timeline. The badge shows how many steps were included.',
          ...noNext,
        },
        ...gateOnClickThenAppear(GATE_STEPS_TAB, '#tour-v2-tab-steps', '#tour-steps-timeline'),
      },
      SESSION_DETAIL_STEPS_TIMELINE_STEP,
    );
  }

  if (document.querySelector('#tour-v2-tab-transcript') !== null) {
    steps.push(
      {
        element: '#tour-v2-tab-transcript',
        popover: {
          title: 'Transcript',
          description: 'Click the Transcript tab to review the full conversation.',
          ...noNext,
        },
        ...gateOnClickThenAppear(GATE_TRANSCRIPT_TAB, '#tour-v2-tab-transcript', '#tour-session-transcript'),
      },
      ...SESSION_DETAIL_TRANSCRIPT_STEPS,
    );
  }

  steps.push(...filterExistingSteps([SESSION_DETAIL_AB_TOGGLE_STEP]), SESSION_DETAIL_CLOSING_STEP);
  return steps;
}

const MY_PROGRESS_INTRO_STEPS: DriveStep[] = [
  {
    element: '#tour-page-title',
    popover: {
      title: 'My Progress',
      description: 'Track your XP, streaks, and badges as you practice.',
      showButtons: ['next', 'close'],
    },
  },
  {
    element: '#tour-page-primary-action',
    popover: {
      title: 'Tabs',
      description: 'Switch between Overview, Leaderboard, and Badges.',
    },
  },
];

const MY_PROGRESS_OVERVIEW_STEPS: DriveStep[] = [
  {
    element: '#tour-page-content',
    popover: {
      title: 'Profile Banner',
      description: 'A quick snapshot of your training profile.',
    },
  },
  {
    element: '#tour-progress-stats',
    popover: {
      title: 'Stats at a Glance',
      description: 'Your total XP, streak, badges, and sessions.',
    },
  },
  {
    element: '#tour-progress-streak',
    popover: {
      title: 'Practice Streak',
      description: 'Keep your practice streak alive by training regularly. It resets if you go too long without a session.',
    },
  },
  {
    element: '#tour-progress-xp-level',
    popover: {
      title: 'XP & Level',
      description: 'Track your XP progress toward the next level as you complete sessions.',
    },
  },
  {
    element: '#tour-progress-next-badges',
    popover: {
      title: 'Next Badges',
      description: 'See how close you are to earning your next badges.',
    },
  },
  {
    element: '#tour-progress-activity-heatmap',
    popover: {
      title: 'Activity',
      description: 'A day-by-day view of your practice activity over the year.',
    },
  },
  {
    element: '#tour-progress-difficulty-breakdown',
    popover: {
      title: 'Sessions by Difficulty',
      description: 'A breakdown of your completed sessions by difficulty level.',
      showButtons: ['next', 'close'],
      doneBtnText: 'Finish',
    },
  },
];

// The leaderboard header's own text already differs by role (see
// MyProgressPage.tsx: "Rankings across all orgs" for super admins vs.
// "Rankings at {org}" for everyone else), so the tour copy reads that same
// element rather than guessing the viewer's role independently — keeps the
// two in sync without pageSteps.ts knowing anything about roles.
function buildLeaderboardSteps(): DriveStep[] {
  const headerText = document.querySelector('#tour-progress-leaderboard-header')?.textContent ?? '';
  const scope = headerText.includes('across all orgs') ? 'all organizations' : 'your organization';

  return [
    {
      element: '#tour-progress-leaderboard-header',
      popover: {
        title: 'Rankings',
        description: `See how you rank against others in ${scope}, and switch between this week, this month, or all time.`,
      },
    },
    {
      element: '#tour-progress-leaderboard-table',
      popover: {
        title: 'Leaderboard',
        description: 'The top performers appear on the podium, with the full ranked list below.',
        showButtons: ['next', 'close'],
        doneBtnText: 'Finish',
      },
    },
  ];
}

const MY_PROGRESS_BADGES_STEPS: DriveStep[] = [
  {
    element: '#tour-progress-badges-header',
    popover: {
      title: 'Milestones',
      description: 'Track how many badges you’ve earned out of the full catalogue.',
    },
  },
  {
    element: '#tour-progress-badges-grid',
    popover: {
      title: 'Badges',
      description: 'Earned badges are highlighted; locked ones show what you need to do to unlock them.',
      showButtons: ['next', 'close'],
      doneBtnText: 'Finish',
    },
  },
];

// Overview, Leaderboard, and Badges are separate tabs whose content is
// conditionally rendered (not just hidden) — only the active tab's elements
// exist in the DOM. A single static step list would only ever show whichever
// tab happened to be open when the tour starts, so the tab-specific steps are
// picked based on which tab's content is actually present at tour-open time.
function buildMyProgressSteps(): DriveStep[] {
  const steps = filterExistingSteps(MY_PROGRESS_INTRO_STEPS);

  if (document.querySelector('#tour-progress-leaderboard-header') !== null) {
    return [...steps, ...filterExistingSteps(buildLeaderboardSteps())];
  }
  if (document.querySelector('#tour-progress-badges-header') !== null) {
    return [...steps, ...filterExistingSteps(MY_PROGRESS_BADGES_STEPS)];
  }
  return [...steps, ...filterExistingSteps(MY_PROGRESS_OVERVIEW_STEPS)];
}

const ANALYTICS_DASHBOARD_STEPS: DriveStep[] = [
  {
    element: '#tour-page-title',
    popover: {
      title: 'Analytics Dashboard',
      description: 'View analytics and reporting across your organization.',
      showButtons: ['next', 'close'],
    },
  },
  {
    element: '#tour-page-primary-action',
    popover: {
      title: 'Refresh',
      description: 'Refresh the analytics data.',
    },
  },
  {
    element: '#tour-filter-bar',
    popover: {
      title: 'Filters',
      description: 'Narrow the data down by date range, org, or other criteria.',
    },
  },
  {
    element: '#tour-kpi-grid',
    popover: {
      title: 'KPIs',
      description: 'Key numbers summarizing training performance at a glance.',
    },
  },
  {
    element: '#tour-page-content',
    popover: {
      title: 'Charts',
      description: 'Explore training performance and session outcomes in more detail here.',
      showButtons: ['next', 'close'],
      doneBtnText: 'Finish',
    },
  },
];

const USERS_STEPS: DriveStep[] = [
  {
    element: '#tour-page-title',
    popover: {
      title: 'Users',
      description: 'Manage the users in your organization.',
      showButtons: ['next', 'close'],
    },
  },
  {
    // Sits left of Add User in the header, so it is toured first.
    // Admins and super admins only — filterExistingSteps drops it for everyone else.
    element: '#tour-users-import',
    popover: {
      title: 'Import Users',
      description:
        'Create many users at once from a CSV or Excel file (name, email, role, organization, user_from, trial). ' +
        'You can download a template, and a preview shows the organization each row will go into before anything is created. ' +
        'Rows with errors, or whose organization is not found, are skipped.',
    },
  },
  {
    element: '#tour-page-primary-action',
    popover: {
      title: 'Add User',
      description: 'Invite or create a new user.',
    },
  },
  {
    element: '#tour-page-content',
    popover: {
      title: 'User List',
      description: 'Browse and manage existing users here.',
    },
  },
  {
    // The per-row cell, not the `<th>` header — on narrow viewports the
    // table scrolls horizontally to reach the Actions column (see
    // UsersPage.scss), and the header can end up off-screen or (mirroring
    // the Assignments pages' same fix below) scrolled to unreliably. The
    // per-row cell is what's actually being scrolled into view.
    //
    // Scoped under `#tour-page-content` (not a bare `.actions-cell`) — the
    // loading skeleton's placeholder row (UserTableSkeleton in
    // UsersPage.tsx) reuses the exact same `.actions-cell` class, and
    // `document.querySelector` returns the first match in document order.
    // Only the real table's container carries `#tour-page-content`, so this
    // can never resolve to the skeleton even if the tutorial is opened while
    // the list is still loading.
    element: '#tour-page-content .actions-cell',
    popover: {
      title: 'Actions',
      description: 'Edit, remove or delete a user from the Actions column on each row. Removing schedules deletion in 2 years and can be undone.',
    },
  },
  withCenteredHighlight({
    element: '#tour-users-pagination',
    popover: {
      title: 'Pagination',
      // Pagination bars sit at the bottom of these tables with little to no
      // room below for driver.js's default placement, which otherwise
      // overlaps the highlighted control instead of flipping above it.
      side: 'top',
      description: 'Move between pages, or change how many users are shown per page.',
      showButtons: ['next', 'close'],
      doneBtnText: 'Finish',
    },
  }),
];

const ORGANIZATIONS_STEPS: DriveStep[] = [
  {
    element: '#tour-page-title',
    popover: {
      title: 'Organizations',
      description: 'Manage organizations and their sub-organizations.',
      showButtons: ['next', 'close'],
    },
  },
  {
    // Sits left of Add Organization in the header, so it is toured first.
    element: '#tour-organizations-import',
    popover: {
      title: 'Import Organizations',
      description:
        'Create many organizations at once from a CSV or Excel file in the AgileEd district/school format (Name, State, ' +
        'Address Line 1, City, Zip, AgileEd Id, Parent AgileEd Id, Parent Name). You can download a template, and a preview ' +
        'checks every row before anything is created. Rows with errors, or whose name already exists, are skipped.',
    },
  },
  {
    element: '#tour-page-primary-action',
    popover: {
      title: 'Add Organization',
      description: 'Create a new organization.',
    },
  },
  {
    element: '#tour-page-content',
    popover: {
      title: 'Organization List',
      description:
        'Browse and manage organizations here. Select a column heading to sort the whole list, ' +
        'or use the funnel icon on a heading to filter by location, tier, seats or notifications. ' +
        'Export gives you the filtered list as a CSV.',
    },
  },
  {
    // The per-row cell, not the `<th>` header — and scoped under
    // `#tour-page-content` for the same reason as the Users page's Actions
    // step above (OrganizationPage.tsx's TableSkeleton reuses the same
    // `.actions-cell` class as the real table).
    element: '#tour-page-content .actions-cell',
    popover: {
      title: 'Actions',
      description: 'Edit or delete an organization from the Actions column on each row.',
    },
  },
  withCenteredHighlight({
    element: '#tour-organizations-pagination',
    popover: {
      title: 'Pagination',
      // Pagination bars sit at the bottom of these tables with little to no
      // room below for driver.js's default placement, which otherwise
      // overlaps the highlighted control instead of flipping above it.
      side: 'top',
      description: 'Move between pages, or change how many organizations are shown per page.',
      showButtons: ['next', 'close'],
      doneBtnText: 'Finish',
    },
  }),
];

const SCENE_EDITOR_INTRO_STEPS: DriveStep[] = [
  {
    element: '#tour-page-title',
    popover: {
      title: 'Scene Editor',
      description: 'Configure the 3D classroom scenes used in roleplay sessions.',
      showButtons: ['next', 'close'],
    },
  },
  {
    element: '#tour-page-primary-action',
    popover: {
      title: 'Prompt Config',
      description: 'Choose which prompt configuration to edit the scene for.',
    },
  },
  {
    element: '#tour-page-content',
    popover: {
      title: 'Scene Viewport',
      description: 'Preview the 3D classroom scene here.',
    },
  },
];

interface SceneToolbarTab {
  id: 'animate' | 'camera' | 'assets' | 'layout';
  label: string;
  description: string;
}

// The Animate/Camera/Assets/Layout tab bodies are all mounted at once inside
// `#tour-scene-content` (see SceneToolbar.tsx), toggled via `display` rather
// than conditional rendering — only one is ever visible at a time. A single
// step highlighting the shared container can't describe all four tools
// accurately (whichever tab happens to be open is the only one actually
// visible), so each tab gets its own click-gated step targeting its own
// content wrapper (`#tour-scene-tab-<id>`).
const SCENE_TOOLBAR_TABS: SceneToolbarTab[] = [
  { id: 'animate', label: 'Animate', description: 'Choose calm and aggressive animation sets, idle poses, and the animations used during roleplay.' },
  { id: 'camera', label: 'Camera', description: 'Adjust the camera position and framing for this scene.' },
  { id: 'assets', label: 'Assets', description: 'Manage the 3D assets used in this scene.' },
  { id: 'layout', label: 'Layout', description: 'Toggle edit mode, then translate, rotate, or scale objects directly in the scene.' },
];

/**
 * The scene toolbar (`#tour-scene-toolbar`) always exists in the DOM, but
 * starts collapsed — its content panel only renders once a tab is clicked
 * (see SceneToolbar.tsx's `isOpen` state). This walks all four tabs in order,
 * gating each on a click of that tab's button (mirroring the FTUE/Role Play
 * tours) before highlighting its now-visible content. Only the first tab in
 * the sequence can already be open when the tour starts (clicking a tab
 * always activates it — later tabs are guaranteed inactive once an earlier
 * one in this sequence has just been clicked), so only it needs an
 * already-active check to skip a redundant (and toggle-closing) click gate.
 */
function buildSceneEditorSteps(driverRef: React.RefObject<Driver | null>): DriveStep[] {
  const { gateOnClickThenAppear } = createGates(driverRef);
  const introSteps = filterExistingSteps(SCENE_EDITOR_INTRO_STEPS);

  const steps: DriveStep[] = [...introSteps];

  SCENE_TOOLBAR_TABS.forEach((tab, i) => {
    const buttonSelector = `[data-testid="tab-${tab.id}"]`;
    const activeSelector = `${buttonSelector}.st__tab--active`;
    const alreadyActive = document.querySelector(activeSelector) !== null;

    if (!(i === 0 && alreadyActive)) {
      steps.push({
        element: buttonSelector,
        popover: {
          title: 'Scene Editor Tools',
          description: `Click the ${tab.label} tab to open it.`,
          ...noNext,
        },
        ...gateOnClickThenAppear(i, buttonSelector, activeSelector),
      });
    }

    const isLast = i === SCENE_TOOLBAR_TABS.length - 1;
    steps.push({
      element: `#tour-scene-tab-${tab.id}`,
      popover: {
        title: `${tab.label} Tools`,
        description: tab.description,
        // The toolbar panel (SceneToolbar.scss's `.st--open`) is right-docked
        // and spans the full height of `.scene-viewport`, but that viewport
        // itself starts below the page title/admin bar — real vertical room
        // exists there for driver.js's own placement logic to dock the
        // popover above the panel without overlapping it. An earlier fixed
        // top/left CSS override (forcing position regardless of this step's
        // actual content height) fought that and re-introduced the overlap
        // on the tallest of these four steps (the last one, with its extra
        // Finish-button footer) — prefer 'top' explicitly instead and let
        // driver.js measure real available space rather than guessing it.
        side: 'top',
        align: 'start',
        ...(isLast ? { showButtons: ['next', 'close'], doneBtnText: 'Finish' } : {}),
      },
    });
  });

  return steps;
}

const APP_FEEDBACK_STEPS: DriveStep[] = [
  {
    element: '#tour-page-title',
    popover: {
      title: 'App Feedback Management',
      description: 'See feedback that users have submitted about the app.',
      showButtons: ['next', 'close'],
    },
  },
  {
    element: '#tour-page-primary-action',
    popover: {
      title: 'Filters',
      description: 'Filter feedback submissions.',
    },
  },
  {
    element: '#tour-page-content',
    popover: {
      title: 'Feedback List',
      description: 'Review submitted feedback here.',
    },
  },
  {
    element: '#tour-feedback-actions-header',
    popover: {
      title: 'Actions',
      description: 'View details, edit status, or delete a feedback submission from the Actions column.',
    },
  },
  withCenteredHighlight({
    element: '#tour-feedback-pagination',
    popover: {
      title: 'Pagination',
      // Pagination bars sit at the bottom of these tables with little to no
      // room below for driver.js's default placement, which otherwise
      // overlaps the highlighted control instead of flipping above it.
      side: 'top',
      description: 'Move between pages, or change how many feedback submissions are shown per page.',
      showButtons: ['next', 'close'],
      doneBtnText: 'Finish',
    },
  }),
];

const PROMPT_STUDIO_STEPS: DriveStep[] = [
  {
    element: '#tour-page-title',
    popover: {
      title: 'Prompt Studio',
      description: 'Author and edit the AI prompts that drive roleplay scenarios.',
      showButtons: ['next', 'close'],
    },
  },
  {
    element: '#tour-page-primary-action',
    popover: {
      title: 'Tabs',
      description: 'Switch between Prompt Configs, Content Blocks, Templates, and Evaluations.',
    },
  },
  {
    element: '#tour-prompt-create',
    popover: {
      title: 'Create',
      // Overwritten per-tab by buildPromptStudioSteps — see PROMPT_STUDIO_CREATE_COPY.
      description: 'Click here to create a new prompt config.',
    },
  },
  {
    // id passed to every Prompt Studio tab's <StatusFilter id="tour-prompt-filter" ...>.
    element: '#tour-prompt-filter',
    popover: {
      title: 'Filter',
      description: 'Filter the list by Published, Draft, or Archived status.',
    },
  },
  {
    element: '#tour-page-content',
    popover: {
      title: 'Editor',
      description: 'Build and configure items here.',
    },
  },
  {
    // Root class of EntityCard.tsx's actions row, present on every card in the
    // list — driver.js highlights the first match as a representative example.
    // Not present on the Evaluations tab (its cards use a different markup),
    // so filterExistingSteps drops this step there — see buildPromptStudioSteps.
    element: '.pc-entity-card__actions-row',
    popover: {
      title: 'Item Actions',
      // Role- and item-dependent, so this is only the fallback: buildPromptStudioSteps
      // narrows it to the buttons actually rendered. Anything said here has to hold for
      // every viewer, because it is what shows when the DOM cannot be read.
      description:
        'Icon buttons for managing each item. Which ones appear depends on your role and on '
        + 'whether the item is shared across organizations.',
      showButtons: ['next', 'close'],
      doneBtnText: 'Finish',
    },
  },
];

// Each Prompt Studio tab renders a different embedded view with its own
// create action (see PromptConfigsView/BuildingBlocksView/SchemaManagerView/
// EvalSuitesView), but they all share the same `#tour-prompt-create` button —
// so its popover copy must match whichever tab is actually active rather than
// listing all four generically (which also previously omitted Evaluations
// entirely, since it wasn't part of the original static description).
const PROMPT_STUDIO_CREATE_COPY: Record<string, string> = {
  prompts: 'Click here to create a new prompt config.',
  'content-blocks': 'Click here to create a new content block.',
  templates: 'Click here to create a new template.',
  evaluations: 'Click here to create a new evaluation suite.',
};

function buildPromptStudioSteps(): DriveStep[] {
  const activeTab = new URLSearchParams(window.location.search).get('tab') ?? 'prompts';
  const createDescription = PROMPT_STUDIO_CREATE_COPY[activeTab] ?? PROMPT_STUDIO_CREATE_COPY.prompts;

  const steps = filterExistingSteps(
    PROMPT_STUDIO_STEPS.map((step) =>
      step.element === '#tour-prompt-create'
        ? { ...step, popover: { ...step.popover, description: createDescription } }
        : step
    )
  );

  // The Evaluations tab lacks the filter and edit/delete steps' anchors, so
  // they're filtered out above — make sure whichever step actually ends up
  // last still closes the tour properly.
  const last = steps[steps.length - 1];
  if (last?.popover) {
    last.popover = { ...last.popover, showButtons: ['next', 'close'], doneBtnText: 'Finish' };
  }

  return describeCardActions(steps);
}

/**
 * Narrow the card-actions step to the buttons this viewer can actually see.
 *
 * EntityCard renders its actions per row, from the viewer's role and the item's
 * ownership: an org admin gets Duplicate plus Hide/Unhide on a platform item and
 * Edit/Delete on their own, while a super admin gets Duplicate/Edit/Delete and never
 * Hide (`canHidePlatform` is `userRole === 'admin'`). filterExistingSteps only checks
 * that the row itself exists, so fixed copy would name controls the viewer does not
 * have and deny ones they do. Same approach as buildReviewerDashboardSteps.
 */
function describeCardActions(steps: DriveStep[]): DriveStep[] {
  const titles = new Set(
    Array.from(document.querySelectorAll('.pc-entity-card__actions-row button')).map((button) =>
      button.getAttribute('title')?.trim(),
    ),
  );

  const clauses: string[] = [];
  if (titles.has('Edit')) clauses.push('edit an item');
  if (titles.has('Duplicate')) clauses.push('duplicate one into your own organization');
  if (titles.has('Hide from my organization')) clauses.push('hide a platform item from your users');
  if (titles.has('Unhide for my organization')) clauses.push('bring back one you hid');
  if (titles.has('Delete')) clauses.push('delete one you own');

  // No cards, or no recognised buttons: keep the general copy rather than narrowing it
  // to something possibly wrong.
  if (clauses.length === 0) return steps;

  return reviseStep(steps, '.pc-entity-card__actions-row', {
    description: `${sentence(joinClauses(clauses))}.`,
  });
}

const REVIEW_MANAGEMENT_STEPS: DriveStep[] = [
  {
    element: '#tour-page-title',
    popover: {
      title: 'Reviews',
      description: 'Manage human-review requests for roleplay sessions.',
      showButtons: ['next', 'close'],
    },
  },
  {
    element: '#tour-page-primary-action',
    popover: {
      title: 'Search',
      description: 'Search review requests by module or learner.',
    },
  },
  {
    element: '#tour-review-tabs',
    popover: {
      title: 'Filter by Status',
      description: 'Narrow the list down to All, Pending, Assigned, In Progress, or Completed reviews.',
    },
  },
  {
    element: '#tour-page-content',
    popover: {
      title: 'Review List',
      description: 'Browse review requests here.',
    },
  },
  {
    element: '.review-mgmt__kebab',
    popover: {
      title: 'Actions',
      description: 'View details, assign or add reviewers, set priority, cancel reviewers, or delete a request from its actions menu.',
    },
  },
  withCenteredHighlight({
    element: '#tour-reviews-pagination',
    popover: {
      title: 'Pagination',
      // Pagination bars sit at the bottom of these tables with little to no
      // room below for driver.js's default placement, which otherwise
      // overlaps the highlighted control instead of flipping above it.
      side: 'top',
      description: 'Move between pages, or change how many review requests are shown per page.',
      showButtons: ['next', 'close'],
      doneBtnText: 'Finish',
    },
  }),
];

const REVIEWER_DASHBOARD_STEPS: DriveStep[] = [
  {
    element: '#tour-page-title',
    popover: {
      title: 'My Reviews',
      description: 'Review roleplay sessions assigned to you.',
      showButtons: ['next', 'close'],
    },
  },
  {
    element: '#tour-page-primary-action',
    popover: {
      title: 'Filter by Status',
      description: 'Click a stat card to filter by Pending, Assigned to Me, In Progress, or Completed.',
    },
  },
  {
    element: '#tour-reviewer-toolbar',
    popover: {
      title: 'Search & Filters',
      description: 'Search by session, filter by priority, or narrow down by date range.',
    },
  },
  {
    element: '#tour-page-content',
    popover: {
      title: 'Assigned Sessions',
      // Rewritten by buildReviewerDashboardSteps from the buttons actually on
      // screen; this copy is the all-statuses case.
      description: 'Claim a pending session, work on one assigned to you, or read back a review you have finished.',
      showButtons: ['next', 'close'],
      doneBtnText: 'Finish',
    },
  },
  withCenteredHighlight({
    element: '#tour-reviewer-dashboard-pagination',
    popover: {
      title: 'Pagination',
      // Pagination bars sit at the bottom of these tables with little to no
      // room below for driver.js's default placement, which otherwise
      // overlaps the highlighted control instead of flipping above it.
      side: 'top',
      description: 'Move between pages, or change how many sessions are shown per page.',
      showButtons: ['next', 'close'],
      doneBtnText: 'Finish',
    },
  }),
];

/**
 * One button per row, and which one depends on that row's status (see
 * ReviewerDashboardPage's actions cell): "Assign to me" for pending, "Review"
 * for anything in progress, "View" for completed. The status filter therefore
 * decides what exists — on the Completed card only "View" is ever rendered, so
 * fixed copy naming "Assign to me" and "Review" described two buttons that
 * weren't there and omitted the only one that was.
 */
function buildReviewerDashboardSteps(): DriveStep[] {
  const steps = filterExistingSteps(REVIEWER_DASHBOARD_STEPS);

  const labels = Array.from(document.querySelectorAll('.reviewer-dash__actions button')).map((button) =>
    button.textContent?.trim(),
  );
  const clauses: string[] = [];
  if (labels.includes('Assign to me')) clauses.push('claim a pending session with "Assign to me"');
  if (labels.includes('Review')) clauses.push('open one assigned to you with "Review"');
  if (labels.includes('View')) clauses.push('read back a finished review with "View"');

  // No rows, or an unrecognised set of labels: leave the general copy alone
  // rather than narrowing it to something possibly wrong.
  if (clauses.length === 0) return steps;

  return reviseStep(steps, '#tour-page-content', {
    description: `${sentence(joinClauses(clauses))}.`,
  });
}

interface GenericPageCopy {
  title: { title: string; description: string };
  primaryAction: { title: string; description: string };
  content: { title: string; description: string };
}

// Copy for the lightweight, consistent 3-step tour shared by every other
// nav-linked page: title -> primary action -> main content.
const GENERIC_PAGE_COPY: Record<string, GenericPageCopy> = {
  '/team-progress': {
    title: { title: 'Team Progress', description: 'Track XP, streaks, and progress across all your trainees.' },
    primaryAction: { title: 'Period & Export', description: 'Change the time period, or export the roster as CSV.' },
    content: { title: 'Trainee Roster', description: "See each trainee's progress here." },
  },
};

const MY_ASSIGNMENTS_STEPS: DriveStep[] = [
  {
    element: '#tour-page-title',
    popover: {
      title: 'My Assignments',
      description: 'Track your assigned training bundles and progress.',
      showButtons: ['next', 'close'],
    },
  },
  {
    element: '#tour-page-primary-action',
    popover: {
      title: 'Search',
      description: 'Search your assignments.',
    },
  },
  {
    element: '#tour-page-content',
    popover: {
      title: 'Assignment List',
      description: 'View your assigned training here.',
    },
  },
  {
    // Not the '#tour-my-assignments-actions-header' <th> — that's the table's
    // header row, which _assignment-shared.scss visually-hides (not
    // display:none) below 720px, so it's still DOM-present but collapses to
    // a near-zero-size box in the mobile card layout, breaking the highlight.
    // The row-level `.assign-actions-cell` <td> is visible in both layouts.
    element: '.assign-actions-cell',
    popover: {
      title: 'Actions',
      description: 'Click Start or Review to begin or revisit an assignment.',
    },
  },
  withCenteredHighlight({
    element: '#tour-my-assignments-pagination',
    popover: {
      title: 'Pagination',
      // Pagination bars sit at the bottom of these tables with little to no
      // room below for driver.js's default placement, which otherwise
      // overlaps the highlighted control instead of flipping above it.
      side: 'top',
      description: 'Move between pages, or change how many assignments are shown per page.',
      showButtons: ['next', 'close'],
      doneBtnText: 'Finish',
    },
  }),
];

const ASSIGNMENTS_STEPS: DriveStep[] = [
  {
    element: '#tour-page-title',
    popover: {
      title: 'Assignments',
      description: 'Create and manage training assignments for your organization.',
      showButtons: ['next', 'close'],
    },
  },
  {
    element: '#tour-page-primary-action',
    popover: {
      title: 'New Assignment',
      description: 'Create a new assignment.',
    },
  },
  {
    element: '#tour-page-content',
    popover: {
      title: 'Assignment List',
      description: 'Browse and manage assignments here.',
    },
  },
  {
    // See the comment on the equivalent My Assignments step above —
    // '.assign-actions-cell' (not the visually-hidden <th> below 720px).
    element: '.assign-actions-cell',
    popover: {
      title: 'Actions',
      description: 'View, edit, publish, or archive an assignment from the Actions column.',
    },
  },
  withCenteredHighlight({
    element: '#tour-assignments-pagination',
    popover: {
      title: 'Pagination',
      // Pagination bars sit at the bottom of these tables with little to no
      // room below for driver.js's default placement, which otherwise
      // overlaps the highlighted control instead of flipping above it.
      side: 'top',
      description: 'Move between pages, or change how many assignments are shown per page.',
      showButtons: ['next', 'close'],
      doneBtnText: 'Finish',
    },
  }),
];

const BUNDLES_STEPS: DriveStep[] = [
  {
    element: '#tour-page-title',
    popover: {
      title: 'Bundles',
      description: 'Create and manage content bundles for assignments.',
      showButtons: ['next', 'close'],
    },
  },
  {
    element: '#tour-page-primary-action',
    popover: {
      title: 'New Bundle',
      description: 'Create a new bundle.',
    },
  },
  {
    element: '#tour-page-content',
    popover: {
      title: 'Bundle List',
      description: 'Browse and manage bundles here.',
    },
  },
  {
    // See the comment on the equivalent My Assignments step above —
    // '.assign-actions-cell' (not the visually-hidden <th> below 720px).
    element: '.assign-actions-cell',
    popover: {
      title: 'Actions',
      description: 'Edit, publish, or archive a bundle from the Actions column.',
    },
  },
  withCenteredHighlight({
    element: '#tour-bundles-pagination',
    popover: {
      title: 'Pagination',
      // Pagination bars sit at the bottom of these tables with little to no
      // room below for driver.js's default placement, which otherwise
      // overlaps the highlighted control instead of flipping above it.
      side: 'top',
      description: 'Move between pages, or change how many bundles are shown per page.',
      showButtons: ['next', 'close'],
      doneBtnText: 'Finish',
    },
  }),
];

const COMPETITIONS_INTRO_STEPS: DriveStep[] = [
  {
    element: '#tour-page-title',
    popover: {
      title: 'Competitions',
      description: 'Join or manage competitions and track leaderboards.',
      showButtons: ['next', 'close'],
    },
  },
  {
    element: '#tour-page-primary-action',
    popover: {
      title: 'Schedule Competition',
      description: 'Create a new competition with a time window, target org, and optional prompt set.',
    },
  },
  {
    element: '#tour-competitions-tabs',
    popover: {
      title: 'Tabs',
      description: 'Switch between live Competitions and Ended Competitions.',
    },
  },
  {
    element: '#tour-page-content',
    popover: {
      title: 'Competition List',
      description: 'Browse competitions here. See their status, window, and organization.',
    },
  },
];

// The Actions column header always renders once the table does, but which
// row-level buttons appear under it depends on both the active tab and the
// viewer's relationship to each competition (see CompetitionsPage.tsx's
// actions-cell): Start/Continue is hidden for competitions the current user
// created, Edit and Cancel/End-Now only show for a manager on a scheduled/
// active, non-read-only competition — so on the Ended Competitions tab none
// of them ever appear, leaving only the Leaderboard button.
// filterExistingSteps' DOM-presence check on the header alone can't
// see any of this, so the copy is built from which action buttons are
// actually present, not just whether the header exists.
function buildCompetitionsSteps(): DriveStep[] {
  const steps = filterExistingSteps(COMPETITIONS_INTRO_STEPS);

  if (document.querySelector('#tour-competitions-actions-header') === null) {
    return steps;
  }

  const hasStartOrContinue = document.querySelector('.edit-button[title="Start"], .edit-button[title="Continue"]') !== null;
  const hasEdit = document.querySelector('.edit-button[title="Edit competition"]') !== null;
  const hasCancelOrEnd =
    document.querySelector('.delete-button[title="Cancel competition"], .delete-button[title="End competition now"]') !== null;

  const clauses = ['view its leaderboard'];
  if (hasStartOrContinue) clauses.unshift('start or continue a competition');
  if (hasEdit) clauses.push('edit it');
  if (hasCancelOrEnd) clauses.push('cancel/end it early');

  const description = `${sentence(joinClauses(clauses))} from the Actions column.`;

  return [
    ...steps,
    {
      element: '#tour-competitions-actions-header',
      popover: {
        title: 'Actions',
        description,
        showButtons: ['next', 'close'],
        doneBtnText: 'Finish',
      },
    },
  ];
}

const CHARACTERS_STEPS: DriveStep[] = [
  {
    element: '#tour-page-title',
    popover: {
      title: 'Characters',
      description: 'The AI students used in roleplay. A character can be shared platform-wide or scoped to one organization.',
      showButtons: ['next', 'close'],
    },
  },
  {
    element: '#tour-page-primary-action',
    popover: {
      title: 'Add a Character',
      description: 'Create a new character with a name, age, gender, Gemini voice and avatar, then build out their personality.',
    },
  },
  {
    element: '#tour-manage-social-skills',
    popover: {
      title: 'Social Skills',
      description: 'Create the social skills a character can be scored on and say what each one means — the description is what the AI student is told. Platform-wide skills are shared with everyone; an organization\'s own skills stay private to it.',
    },
  },
  {
    element: '#tour-characters-rail',
    popover: {
      title: 'Find a Character',
      description: 'Search by name, or filter to the characters that already have voice tuning.',
    },
  },
  {
    // The detail pane, not the tab strip inside it: the tabs only render once a
    // character is selected, and an empty roster would strip the step entirely.
    element: '#tour-page-content',
    popover: {
      title: 'Character Settings',
      description: 'Tune voice, personality (Big Five, attachment style, social skills) here; change the avatar from Edit. A character must be Published before trainees and trainers can select it in a roleplay session, and a scene can only seat characters from its own organization (or platform-wide ones).',
      showButtons: ['next', 'close'],
      doneBtnText: 'Finish',
    },
  },
];

const PLATFORM_DASHBOARD_STEPS: DriveStep[] = [
  {
    element: '.platform-dash-stat-grid',
    popover: {
      title: 'Platform Overview',
      description: 'High-level stats across the whole platform: total organizations, users, sessions, and XP earned.',
      showButtons: ['next', 'close'],
    },
  },
  {
    element: '#tour-page-primary-action',
    popover: {
      title: 'Tabs',
      description: 'Switch between Organizations and Users.',
    },
  },
  {
    element: '#tour-page-content',
    popover: {
      title: 'Details',
      description:
        'Explore organization and user detail here. Select a column heading to sort, or use the ' +
        'funnel icon on a heading to filter. Hover a heading to see whether it covers the whole ' +
        'platform or only the page you are on.',
      showButtons: ['next', 'close'],
      doneBtnText: 'Finish',
    },
  },
];

/**
 * Home dashboard catalog. Home renders a different composition per role
 * (trainee / trainer / admin / super_admin), so there is no single fixed step
 * list that fits everyone. Rather than duplicating the role switch from
 * HomePage.tsx here (a second source of truth that would drift), this is a
 * superset catalog: every block any role can see gets an entry, and
 * buildHomeSteps drops the ones absent from the DOM.
 *
 * Safe to filter on DOM presence because Home renders its whole composition
 * at mount. Nothing here is progressive or behind a click, unlike the Role
 * Play wizard tour, so there is no need for gates.
 */
const HOME_STEPS: DriveStep[] = [
  {
    element: '#tour-home-greeting',
    popover: {
      title: 'Your Home',
      description: 'Home shows you the current state of your work. What it shows depends on your role, so this tour covers the blocks on your screen.',
      showButtons: ['next', 'close'],
    },
  },
  {
    element: '#tour-home-hero',
    popover: {
      title: 'Start Practising',
      description: 'Jump straight into a role play, or pick up the assignment you last left unfinished. Your weekly session goal sits alongside it.',
    },
  },
  {
    element: '#tour-home-practice-strip',
    popover: {
      title: 'Your Practice',
      description: 'Your own weekly practice progress. Start a role play from here whenever you want to try a scenario yourself.',
    },
  },
  {
    element: '#tour-home-attention',
    popover: {
      title: 'Needs Your Attention',
      description: 'Anything waiting on a person: sessions to review, trainees who have gone quiet, overdue assignments, and unused seats. Each card links straight to the work.',
    },
  },
  {
    element: '#tour-home-all-clear',
    popover: {
      title: 'All Clear',
      description: 'Nothing is waiting on you right now. When a review backlog, an overdue assignment, or an inactive trainee appears, this block is replaced by the items that need you.',
    },
  },
  {
    element: '#tour-home-momentum',
    popover: {
      title: 'Your Momentum',
      description: 'Your streak, badges, sessions, and XP at a glance. Open My Progress for the full breakdown.',
    },
  },
  {
    element: '#tour-home-team',
    popover: {
      title: 'Team Snapshot',
      description: 'How your trainees are doing this week: how many are active, how many sessions they have run, and the best streak on the team.',
    },
  },
  {
    element: '#tour-home-org-health',
    popover: {
      title: 'Organization Health',
      description: 'Seat usage, user count, weekly activity, and overall assignment completion for your organization.',
    },
  },
  {
    element: '#tour-home-platform',
    popover: {
      title: 'Platform Snapshot',
      description: 'Totals across every organization on the platform.',
    },
  },
  {
    element: '#tour-home-top-orgs',
    popover: {
      title: 'Most Active Organizations',
      description: 'The organizations running the most sessions. Select any row to manage it.',
    },
  },
  {
    element: '#tour-home-overdue',
    popover: {
      title: 'Overdue',
      description: 'Assignments whose due date has already passed and that you have not finished. They are still open. Continue picks up where you left off.',
    },
  },
  {
    element: '#tour-home-assignments',
    popover: {
      title: 'Ongoing Assignments',
      description: 'Assignments you have started or not yet begun and that are not yet past due, with progress on each. Continue takes you into the role play with that assignment already selected.',
    },
  },
  {
    element: '#tour-home-deadlines',
    popover: {
      title: 'Upcoming Deadlines',
      description: 'Assignments with a due date still ahead of you, soonest first. Anything due within two days is flagged in red.',
    },
  },
  {
    element: '#tour-home-actions',
    popover: {
      title: 'Quick Actions',
      description: 'Shortcuts to the tasks your role performs most often.',
    },
  },
];

/**
 * Orders the surviving Home steps by their real position in the document.
 *
 * The catalog above cannot be written in one "correct" order, because each
 * role stacks these blocks differently (a trainee leads with the practice
 * hero, an admin leads with org health). Sorting by document position means
 * the tour always walks down the page in the order the user actually sees,
 * for every role, without this file knowing anything about roles.
 */
function buildHomeSteps(): DriveStep[] {
  const present = filterExistingSteps(HOME_STEPS);

  const ordered = [...present].sort((a, b) => {
    const elA = document.querySelector(a.element as string);
    const elB = document.querySelector(b.element as string);
    if (!elA || !elB) return 0;
    // DOCUMENT_POSITION_FOLLOWING (4) means elB comes after elA.
    return elA.compareDocumentPosition(elB) & Node.DOCUMENT_POSITION_FOLLOWING ? -1 : 1;
  });

  // Whichever block ends up last for this role is the one that closes the
  // tour, so the Finish button has to be attached after sorting.
  const last = ordered[ordered.length - 1];
  if (last) {
    ordered[ordered.length - 1] = {
      ...last,
      popover: { ...last.popover, showButtons: ['next', 'close'], doneBtnText: 'Finish' },
    };
  }

  return ordered;
}

function buildGenericSteps(copy: GenericPageCopy): DriveStep[] {
  return [
    {
      element: '#tour-page-title',
      popover: { title: copy.title.title, description: copy.title.description, showButtons: ['next', 'close'] },
    },
    {
      element: '#tour-page-primary-action',
      popover: { title: copy.primaryAction.title, description: copy.primaryAction.description },
    },
    {
      element: '#tour-page-content',
      popover: { title: copy.content.title, description: copy.content.description, showButtons: ['next', 'close'], doneBtnText: 'Finish' },
    },
  ];
}

const REPORTS_STEPS: DriveStep[] = [
  {
    element: '.user-report-stat-grid',
    popover: {
      title: 'User Report',
      description: 'Track trial, subscribed, renewal, and abandoned-signup users across the platform.',
      showButtons: ['next', 'close'],
    },
  },
  {
    element: '#tour-page-primary-action',
    popover: {
      title: 'Tabs',
      description: 'Switch between Trial, Subscribed, Renewal, and Abandoned Signup user lists.',
    },
  },
  {
    element: '#tour-page-content',
    popover: {
      title: 'User List',
      description: 'Filter by organization, plan, or joined date, and export the list as CSV.',
      showButtons: ['next', 'close'],
      doneBtnText: 'Finish',
    },
  },
];

const JOIN_REQUESTS_STEPS: DriveStep[] = [
  {
    element: '#tour-page-title',
    popover: {
      title: 'Join Requests',
      description: 'People asking to join your organization, and what was decided before.',
      showButtons: ['next', 'close'],
    },
  },
  {
    element: '#tour-join-settings-toggle',
    popover: {
      title: 'Settings',
      description:
        'Claim your organization\'s email domain, turn on auto-approve, set the trial-seat allowance, and choose who can approve requests.',
    },
  },
  {
    element: '#tour-page-primary-action',
    popover: {
      title: 'Seats',
      description: 'How many seats are free right now — this decides whether Accept is offered.',
    },
  },
  {
    element: '#tour-page-content',
    popover: {
      title: 'Requests',
      description: 'Decide pending requests, and move approved people into a seat once one frees up.',
      showButtons: ['next', 'close'],
      doneBtnText: 'Finish',
    },
  },
];

/**
 * The Settings panel (JoinSettingsCard) toggles via the button above rather
 * than unmounting the queue below it — the seat badge and request tabs stay
 * in the DOM with `display: none` while it's open (JoinRequestsPage.tsx), so
 * filterExistingSteps' presence-only check can't tell they're actually
 * invisible. Highlighting them in that state would target elements driver.js
 * can't measure. So when the toggle is already expanded at tour-open time,
 * those two steps are dropped and the Settings step becomes the closing one
 * instead.
 */
function buildJoinRequestsSteps(): DriveStep[] {
  const steps = filterExistingSteps(JOIN_REQUESTS_STEPS).map((step) => ({ ...step }));
  const settingsOpen =
    document.querySelector('#tour-join-settings-toggle')?.getAttribute('aria-expanded') === 'true';
  if (!settingsOpen) return steps;

  const trimmed = steps.filter(
    (step) => step.element !== '#tour-page-primary-action' && step.element !== '#tour-page-content'
  );
  const last = trimmed[trimmed.length - 1];
  if (last?.popover) {
    last.popover = { ...last.popover, showButtons: ['next', 'close'], doneBtnText: 'Finish' };
  }
  return trimmed;
}

const BESPOKE_STEPS: Record<string, DriveStep[]> = {
  '/dashboard': ANALYTICS_DASHBOARD_STEPS,
  '/users': USERS_STEPS,
  '/organizations': ORGANIZATIONS_STEPS,
  '/reports': REPORTS_STEPS,
  '/feedback': APP_FEEDBACK_STEPS,
  '/reviews': REVIEW_MANAGEMENT_STEPS,
  '/my-assignments': MY_ASSIGNMENTS_STEPS,
  '/assignments': ASSIGNMENTS_STEPS,
  '/bundles': BUNDLES_STEPS,
  '/platform-dashboard': PLATFORM_DASHBOARD_STEPS,
  '/characters': CHARACTERS_STEPS,
};

/**
 * Any step that can legitimately *end* a tour carries the closing treatment
 * statically — `showButtons: ['next', 'close']` plus a Finish label — so the
 * tour still closes properly when the steps after it were filtered out for
 * not being in the DOM (e.g. the reviewer dashboard's session-list step,
 * which is last only when the pagination bar isn't rendered). When those
 * later steps *are* present, that same treatment also hides the step's Back
 * button, stranding the user mid-tour with Next as the only way to move.
 *
 * Put Back on every step except the first (driver.js disables it there
 * anyway, and these tours hide it outright) and the last (the closing step,
 * deliberately Finish/close only). Click-gated tours are left alone: their
 * steps hide Next because the user has to perform the action to advance, and
 * a gate that has already fired can't be re-armed by stepping back into it.
 */
function withBackButtons(steps: DriveStep[]): DriveStep[] {
  const isGated = steps.some((step) => step.popover?.showButtons && !step.popover.showButtons.includes('next'));
  if (isGated) return steps;

  return steps.map((step, index) => {
    const showButtons = step.popover?.showButtons;
    if (index === 0 || index === steps.length - 1) return step;
    if (!showButtons || showButtons.includes('previous')) return step;
    return { ...step, popover: { ...step.popover, showButtons: ['previous', ...showButtons] } };
  });
}

const SESSION_DETAIL_PATTERN = /^\/sessions\/[^/]+$/;

export function getPageSteps(pathname: string, driverRef: React.RefObject<Driver | null>): DriveStep[] {
  return withBackButtons(resolvePageSteps(pathname, driverRef));
}

function resolvePageSteps(pathname: string, driverRef: React.RefObject<Driver | null>): DriveStep[] {
  if (pathname === '/') {
    // '/' is the Home dashboard. Role Play moved to '/roleplay' when Home
    // took over the landing route, so the Role Play tour moved with it.
    return buildHomeSteps();
  }
  if (pathname === '/roleplay') {
    // Deliberately NOT run through filterExistingSteps: this is a
    // progressive, gated wizard-style tour (mirrors the FTUE interactive
    // tour) whose step targets intentionally don't all exist at once. Each
    // one only appears once the step(s) before it are completed (e.g. the
    // mic button doesn't exist until the classroom step's avatar click
    // connects a session). A static, build-time DOM-presence filter would
    // incorrectly strip out every step beyond whatever's visible right now.
    return buildRolePlayPageSteps(driverRef);
  }
  if (pathname === '/sessions') {
    // Not a plain filterExistingSteps — see buildSessionsListSteps' own comment
    // for why the Actions step's copy depends on more than the header existing.
    return buildSessionsListSteps();
  }
  if (SESSION_DETAIL_PATTERN.test(pathname)) {
    // Not run through filterExistingSteps as a whole — see
    // buildSessionDetailSteps's own comment for why the transcript block
    // needs special handling.
    return buildSessionDetailSteps(driverRef);
  }
  if (pathname === '/scene-editor') {
    // Not run through filterExistingSteps for its tail end — see
    // buildSceneEditorSteps's own comment for why.
    return buildSceneEditorSteps(driverRef);
  }
  if (pathname === '/prompt-studio') {
    // Not a plain BESPOKE_STEPS entry — the Create step's copy depends on
    // which tab is active. See buildPromptStudioSteps's own comment.
    return buildPromptStudioSteps();
  }
  if (pathname === '/competitions') {
    // Not run through filterExistingSteps as a whole — see
    // buildCompetitionsSteps's own comment for why the Actions step needs
    // its own DOM check beyond the header's presence.
    return buildCompetitionsSteps();
  }
  if (pathname === '/reviewer-dashboard') {
    // Not run through filterExistingSteps as a whole — see
    // buildReviewerDashboardSteps' own comment for why the row-action copy
    // depends on which statuses are currently listed.
    return buildReviewerDashboardSteps();
  }
  if (pathname === '/join-requests') {
    // Not a plain BESPOKE_STEPS entry — see buildJoinRequestsSteps' own
    // comment for why the Seats/Requests steps depend on whether the
    // Settings panel is currently open.
    return buildJoinRequestsSteps();
  }
  if (pathname === '/my-progress') {
    // Not run through filterExistingSteps as a whole — see
    // buildMyProgressSteps's own comment for why the Overview/Leaderboard/
    // Badges tabs each need their own step set.
    return buildMyProgressSteps();
  }

  const bespokeSteps = BESPOKE_STEPS[pathname];
  if (bespokeSteps) {
    return filterExistingSteps(bespokeSteps);
  }

  const genericCopy = GENERIC_PAGE_COPY[pathname];
  if (genericCopy) {
    return filterExistingSteps(buildGenericSteps(genericCopy));
  }

  return [];
}
